import { useEffect, useMemo, useState } from "react";
import axios from "axios";

import { AdminLayout } from "../components/AdminLayout";
import { getAuthHeader } from "../context/AuthContext";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import {
  CalendarDays,
  Clock3,
  Loader2,
  Phone,
  Search,
  Users,
  Mail,
  FileText,
  CheckCircle2,
  XCircle,
  TimerReset,
  CircleDashed,
} from "lucide-react";

import { toast } from "sonner";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-700 border-yellow-200",
  confirmed: "bg-blue-100 text-blue-700 border-blue-200",
  completed: "bg-green-100 text-green-700 border-green-200",
  cancelled: "bg-red-100 text-red-700 border-red-200",
};

const STATUS_ICONS = {
  pending: TimerReset,
  confirmed: CheckCircle2,
  completed: CheckCircle2,
  cancelled: XCircle,
};

const formatStatus = (status) => {
  if (!status) return "Pending";

  return status
    .replaceAll("_", " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

export default function ReservationManagementPage() {
  const [reservations, setReservations] = useState([]);
const [loading, setLoading] = useState(true);

const [search, setSearch] = useState("");
const [status, setStatus] = useState("all");

/* NEW STATES */
const [todayOnly, setTodayOnly] = useState(false);

const [page, setPage] = useState(1);
const [limit] = useState(20);

const [stats, setStats] = useState({
  total: 0,
  pending: 0,
  confirmed: 0,
  completed: 0,
});
const [editOpen, setEditOpen] = useState(false);

const [editingReservation, setEditingReservation] =
  useState(null);

const [editForm, setEditForm] = useState({
  customer_name: "",
  customer_phone: "",
  customer_email: "",
  party_size: 1,
  reservation_date: "",
  reservation_time: "",
  notes: "",
  section_id: "",
});
const [reservationSettings, setReservationSettings] =
  useState(null);
const [editSelectedDate, setEditSelectedDate] =
  useState("");

const [editAvailableSlots, setEditAvailableSlots] =
  useState([]);
const [reservationSections, setReservationSections] =
  useState([]);
useEffect(() => {
  if (reservations.length > 0) {
    fetchReservationData();
  }
}, [reservations]);
useEffect(() => {
  fetchReservations();
}, [status, todayOnly, page]);

const fetchReservations = async () => {
  try {
    setLoading(true);

    const params = {
      page,
      limit,
    };

    if (status !== "all") {
      params.status = status;
    }

    if (todayOnly) {
      params.today = true;
    }

    const response = await axios.get(`${API}/reservations`, {
      headers: getAuthHeader(),
      params,
    });
// /api/reservations?start_date=2026-05-20&end_date=2026-05-25
    const data = response.data || [];

    setReservations(data);

    setStats({
      total: data.length,
      pending: data.filter((r) => r.status === "pending").length,
      confirmed: data.filter((r) => r.status === "confirmed").length,
      completed: data.filter((r) => r.status === "completed").length,
    });
  } catch (error) {
    toast.error("Failed to load reservations");
  } finally {
    setLoading(false);
  }
};
const generateTimeSlots = (
  startTime,
  endTime,
  interval
) => {
  const slots = [];

  const start = new Date();
  const end = new Date();

  const [startHour, startMinute] =
    startTime.split(":");

  const [endHour, endMinute] =
    endTime.split(":");

  start.setHours(startHour, startMinute, 0);
  end.setHours(endHour, endMinute, 0);

  while (start < end) {
    slots.push(
      start.toTimeString().slice(0, 5)
    );

    start.setMinutes(
      start.getMinutes() + interval
    );
  }

  return slots;
};

const formatSlotTime = (time) => {
  const [hour, minute] = time.split(":");

  const date = new Date();

  date.setHours(hour);
  date.setMinutes(minute);

  return date.toLocaleTimeString(
    "en-IN",
    {
      hour: "numeric",
      minute: "2-digit",
    }
  );
};

const handleEditDateChange = (
  date,
  preserveTime = false
) => {
  setEditSelectedDate(date);

  setEditForm((prev) => ({
    ...prev,
    reservation_date: date,
    reservation_time: preserveTime
      ? prev.reservation_time
      : "",
  }));

  if (!date || !reservationSettings) {
    setEditAvailableSlots([]);
    return;
  }

  const dateObj = new Date(date);

  const dayName = dateObj
    .toLocaleDateString("en-US", {
      weekday: "long",
    })
    .toLowerCase();

  const dayConfig =
    reservationSettings.reservation_hours.find(
      (day) => day.day === dayName
    );

  if (!dayConfig || !dayConfig.enabled) {
    setEditAvailableSlots([]);
    return;
  }

  const slots = generateTimeSlots(
    dayConfig.start_time,
    dayConfig.end_time,
    reservationSettings.slot_duration_minutes
  );

  const now = new Date();

  const filteredSlots = slots.filter(
    (slot) => {
      const slotDate = new Date(
        `${date}T${slot}:00`
      );

      return slotDate > now;
    }
  );

  setEditAvailableSlots(filteredSlots);
};
const openEditModal = (reservation) => {
  setEditingReservation(reservation);

  const reservationDate = new Date(
    reservation.reservation_date
  );

  const formattedDate =
    reservationDate.toISOString().split("T")[0];

  const formattedTime =
    `${String(reservationDate.getHours()).padStart(2, "0")}:${String(
      reservationDate.getMinutes()
    ).padStart(2, "0")}`;

  setEditSelectedDate(formattedDate);

  setEditForm({
    customer_name:
      reservation.customer_name || "",

    customer_phone:
      reservation.customer_phone || "",

    customer_email:
      reservation.customer_email || "",

    party_size:
      reservation.party_size || 1,

    reservation_date:
      formattedDate,

    reservation_time:
      formattedTime,

    notes:
      reservation.notes || "",

    section_id:
      reservation.section_ids?.[0] || "",
  });

  // load slots without clearing selected time
  if (reservationSettings) {
    const dayName = reservationDate
      .toLocaleDateString("en-US", {
        weekday: "long",
      })
      .toLowerCase();

    const dayConfig =
      reservationSettings.reservation_hours.find(
        (day) =>
          day.day === dayName &&
          day.enabled
      );

    if (dayConfig) {
      const slots = generateTimeSlots(
        dayConfig.start_time,
        dayConfig.end_time,
        reservationSettings.slot_duration_minutes
      );

      setEditAvailableSlots(slots);
    }
  }

  setEditOpen(true);
};  
const updateReservation = async () => {
  try {

    if (!editForm.reservation_time) {
      toast.error(
        "Please select a reservation time"
      );
      return;
    }

    const reservationDateTime =
      `${editForm.reservation_date}T${editForm.reservation_time}:00`;

    const payload = {
      reservation_date:
        reservationDateTime,

      party_size:
        Number(editForm.party_size),

      notes:
        editForm.notes,

      section_ids:
        editForm.section_id
          ? [editForm.section_id]
          : [],
    };

    console.log(payload);

    await axios.put(
      `${API}/reservations/${editingReservation.id}`,
      payload,
      {
        headers: getAuthHeader(),
      }
    );

    toast.success(
      "Reservation updated successfully"
    );

    setEditOpen(false);

    fetchReservations();

  } catch (error) {
    const detail =
      error?.response?.data?.detail;

    toast.error(
      Array.isArray(detail)
        ? detail[0]?.msg
        : detail ||
            "Failed to update reservation"
    );
  }
};
const fetchReservationData = async () => {
  try {
    const [settingsRes, sectionsRes] =
      await Promise.all([
        axios.get(
          `${API}/public/reservations/settings`,
          {
            params: {
              restaurant_id:
                reservations?.[0]
                  ?.restaurant_id,
            },
          }
        ),

        axios.get(
          `${API}/public/tables`,
          {
            params: {
              restaurant_id:
                reservations?.[0]
                  ?.restaurant_id,
            },
          }
        ),
      ]);

    setReservationSettings(
      settingsRes.data
    );

    setReservationSections(
      sectionsRes.data?.data || []
    );
  } catch (error) {
    console.error(
      "Error fetching reservation data:",
      error
    );
  }
};
  const filteredReservations = useMemo(() => {
    if (!search) return reservations;

    return reservations.filter((reservation) => {
      const q = search.toLowerCase();

      return (
        reservation.customer_name?.toLowerCase().includes(q) ||
        reservation.customer_phone?.toLowerCase().includes(q) ||
        reservation.reservation_number?.toLowerCase().includes(q)
      );
    });
  }, [reservations, search]);
const updateReservationStatus = async (
  reservationId,
  newStatus
) => {
  try {
    await axios.patch(
      `${API}/reservations/${reservationId}/status`,
      {},
      {
        headers: getAuthHeader(),
        params: {
          status: newStatus,
        },
      }
    );

    toast.success("Reservation status updated");

    setReservations((prev) =>
      prev.map((item) =>
        item.id === reservationId
          ? {
              ...item,
              status: newStatus,
            }
          : item
      )
    );
  } catch (error) {
    toast.error(
      error?.response?.data?.detail ||
        "Failed to update reservation"
    );
  }
};
  return (
    <AdminLayout>
      <div className="min-h-screen bg-slate-50 p-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5 mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 tracking-tight">
              Reservation Management
            </h1>

            <p className="text-slate-500 mt-2">
              Manage customer reservations and booking status
            </p>
          </div>

          <div className="flex flex-col md:flex-row gap-3">
            {/* Search */}
            <div className="relative w-full md:w-[320px]">
              <Search
                size={18}
                className="absolute left-3 top-3 text-slate-400"
              />

              <Input
                placeholder="Search reservation / phone"
                className="pl-10 bg-white border-slate-200"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {/* Status Filter */}
            <Select
              value={status}
              onValueChange={(value) => {
                setStatus(value);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-full md:w-[200px] bg-white">
                <SelectValue placeholder="Filter Status" />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value="all">All Reservations</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>

            {/* Today Filter */}
            <Button
              variant={todayOnly ? "default" : "outline"}
              onClick={() => {
                setTodayOnly(!todayOnly);
                setPage(1);
              }}
            >
              Today
            </Button>

            <Button
              variant="outline"
              onClick={() => {
                setStatus("all");
                setTodayOnly(false);
                setPage(1);
                fetchReservations();
              }}
            >
              Reset
            </Button>

            <Button onClick={fetchReservations}>
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 mb-8">
          <Card className="rounded-3xl border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">
                    Total Reservations
                  </p>

                  <h2 className="text-3xl font-bold mt-2">
                    {stats.total}
                  </h2>
                </div>

                <div className="h-12 w-12 rounded-2xl bg-slate-100 flex items-center justify-center">
                  <CalendarDays className="text-slate-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-3xl border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">
                    Pending
                  </p>

                  <h2 className="text-3xl font-bold mt-2 text-yellow-600">
                    {stats.pending}
                  </h2>
                </div>

                <div className="h-12 w-12 rounded-2xl bg-yellow-100 flex items-center justify-center">
                  <TimerReset className="text-yellow-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-3xl border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">
                    Confirmed
                  </p>

                  <h2 className="text-3xl font-bold mt-2 text-blue-600">
                    {stats.confirmed}
                  </h2>
                </div>

                <div className="h-12 w-12 rounded-2xl bg-blue-100 flex items-center justify-center">
                  <CheckCircle2 className="text-blue-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-3xl border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">
                    Completed
                  </p>

                  <h2 className="text-3xl font-bold mt-2 text-green-600">
                    {stats.completed}
                  </h2>
                </div>

                <div className="h-12 w-12 rounded-2xl bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="text-green-700" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Reservation List */}
        {loading ? (
          <div className="flex items-center gap-3 text-slate-600">
            <Loader2 className="animate-spin" size={18} />

            Loading reservations...
          </div>
        ) : (
          <div className="space-y-5">
            {filteredReservations.map((reservation) => {
              const StatusIcon =
                STATUS_ICONS[reservation.status] || CircleDashed;

              return (
                <Card
                  key={reservation.id}
                  className="rounded-3xl border-0 shadow-sm hover:shadow-md transition-all duration-200"
                >
                  <CardContent className="p-6">
                    <div className="grid lg:grid-cols-3 gap-8">
                      {/* Left */}
                      <div>
                        <div className="flex items-start justify-between gap-3 mb-4">
                          <div>
                            <p className="text-xs uppercase tracking-wide text-slate-400 font-medium">
                              Reservation Number
                            </p>

                            <h2 className="text-xl font-bold text-slate-900 mt-1">
                              {reservation.reservation_number}
                            </h2>
                          </div>

                          <div
                            className={`px-3 py-1 rounded-full text-xs font-semibold border flex items-center gap-1 ${
                              STATUS_COLORS[reservation.status] ||
                              "bg-slate-100 text-slate-700 border-slate-200"
                            }`}
                          >
                            <StatusIcon size={14} />

                            {formatStatus(reservation.status)}
                          </div>
                        </div>

                        <div className="space-y-3 text-sm text-slate-600">
                          <div className="flex items-center gap-3">
                            <Users size={16} />

                            <span className="font-medium text-slate-800">
                              {reservation.customer_name}
                            </span>
                          </div>

                          <div className="flex items-center gap-3">
                            <Phone size={16} />

                            {reservation.customer_phone}
                          </div>

                          {reservation.customer_email && (
                            <div className="flex items-center gap-3">
                              <Mail size={16} />

                              {reservation.customer_email}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Center */}
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-4">
                          Reservation Details
                        </h3>

                        <div className="space-y-4">
                          <div className="flex items-start gap-3">
                            <CalendarDays
                              size={18}
                              className="text-slate-500 mt-0.5"
                            />

                            <div>
                              <p className="text-sm text-slate-500">
                                Reservation Date
                              </p>

                              <p className="font-semibold text-slate-800">
                                {new Date(
                                  reservation.reservation_date
                                ).toLocaleDateString()}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-start gap-3">
                            <Clock3
                              size={18}
                              className="text-slate-500 mt-0.5"
                            />

                            <div>
                              <p className="text-sm text-slate-500">
                                Reservation Time
                              </p>

                              <p className="font-semibold text-slate-800">
                                {new Date(
                                  reservation.reservation_date
                                ).toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}

                                {" - "}

                                {new Date(
                                  reservation.reservation_end_time
                                ).toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-start gap-3">
                            <Users
                              size={18}
                              className="text-slate-500 mt-0.5"
                            />

                            <div>
                              <p className="text-sm text-slate-500">
                                Party Size
                              </p>

                              <p className="font-semibold text-slate-800">
                                {reservation.party_size} Guests
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Right */}
                      <div className="flex flex-col justify-between">
                        <div>
                          <h3 className="font-semibold text-slate-900 mb-4">
                            Additional Info
                          </h3>

                          <div className="space-y-4">
                            <div>
                              <p className="text-sm text-slate-500">
                                Preferred Seating Area
                              </p>

                              <p className="font-semibold text-slate-800 uppercase">
                                {reservation.section_names[0] || "Not Specified"}
                              </p>
                            </div>
                            <div>
                              <p className="text-sm text-slate-500">
                                Source
                              </p>

                              <p className="font-semibold text-slate-800 uppercase">
                                {reservation.source || "POS"}
                              </p>
                            </div>

                            {reservation.notes && (
                              <div className="flex gap-2">
                                <FileText
                                  size={16}
                                  className="text-slate-500 mt-1"
                                />

                                <div>
                                  <p className="text-sm text-slate-500">
                                    Notes
                                  </p>

                                  <p className="text-sm text-slate-700 leading-relaxed">
                                    {reservation.notes}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                      <div className="pt-6 flex flex-wrap gap-3">
                        {/* Pending Actions */}
                        {["pending", "confirmed"].includes(
                          reservation.status?.toLowerCase()
                        ) && (
                          <Button
                            variant="outline"
                            className="flex-1"
                            onClick={() => openEditModal(reservation)}
                          >
                            Edit
                          </Button>
                        )}
                        {reservation.status === "pending" && (
                          <>
                            <Button
                              className="flex-1"
                              onClick={() =>
                                updateReservationStatus(
                                  reservation.id,
                                  "confirmed"
                                )
                              }
                            >
                              <CheckCircle2 size={16} className="mr-2" />
                              Confirm
                            </Button>

                            <Button
                              variant="destructive"
                              className="flex-1"
                              onClick={() =>
                                updateReservationStatus(
                                  reservation.id,
                                  "cancelled"
                                )
                              }
                            >
                              <XCircle size={16} className="mr-2" />
                              Cancel
                            </Button>
                          </>
                        )}

                        {/* Confirmed Actions */}
                        {reservation.status === "confirmed" && (
                          <>
                            <Button
                              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                              onClick={() =>
                                updateReservationStatus(
                                  reservation.id,
                                  "completed"
                                )
                              }
                            >
                              <CheckCircle2 size={16} className="mr-2" />
                              Seated
                            </Button>
                            <Button
                              variant="destructive"
                              className="flex-1"
                              onClick={() =>
                                updateReservationStatus(
                                  reservation.id,
                                  "no_show"
                                )
                              }
                            >
                              <XCircle size={16} className="mr-2" />
                              No Show
                            </Button>

                            <Button
                              className="flex-1"
                              onClick={() =>
                                updateReservationStatus(
                                  reservation.id,
                                  "cancelled"
                                )
                              }
                            >
                              <XCircle size={16} className="mr-2" />
                              Cancel
                            </Button>
                          </>
                        )}

                        {/* Completed */}
                        {reservation.status === "completed" && (
                          <Button
                            disabled
                            className="w-full"
                          >
                            Reservation Completed
                          </Button>
                        )}

                        {/* Cancelled */}
                        {reservation.status === "cancelled" && (
                          <Button
                            disabled
                            variant="outline"
                            className="w-full"
                          >
                            Reservation Cancelled
                          </Button>
                        )}
                      </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {!loading && filteredReservations.length === 0 && (
              <Card className="rounded-3xl border-0 shadow-sm">
                <CardContent className="p-16 text-center">
                  <CalendarDays
                    size={40}
                    className="mx-auto text-slate-300 mb-4"
                  />

                  <h3 className="text-lg font-semibold text-slate-700">
                    No Reservations Found
                  </h3>

                  <p className="text-slate-500 mt-2">
                    No reservation records available for the selected filter
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
        <div className="flex items-center justify-center gap-3 mt-8">
          <Button
            variant="outline"
            disabled={page === 1}
            onClick={() => setPage((prev) => prev - 1)}
          >
            Previous
          </Button>

          <div className="text-sm text-slate-600 font-medium">
            Page {page}
          </div>

          <Button
            variant="outline"
            disabled={reservations.length < limit}
            onClick={() => setPage((prev) => prev + 1)}
          >
            Next
          </Button>
        </div>
        <Dialog
  open={editOpen}
  onOpenChange={setEditOpen}
>
  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
    <DialogHeader>
      <DialogTitle>
        Edit Reservation
      </DialogTitle>
    </DialogHeader>

    <div className="space-y-6">

      {/* DATE SELECTION */}
      <div>
        <label className="text-sm font-semibold text-gray-700 mb-3 block">
          Select Reservation Date
        </label>

        <div className="grid grid-cols-4 gap-2">
          {[...Array(7)].map((_, index) => {
            const date = new Date();

            date.setDate(date.getDate() + index);

            const formattedDate = `${date.getFullYear()}-${String(
              date.getMonth() + 1
            ).padStart(2, "0")}-${String(
              date.getDate()
            ).padStart(2, "0")}`;

            const dayName = date
              .toLocaleDateString("en-US", {
                weekday: "long",
              })
              .toLowerCase();

            const isAvailable =
              reservationSettings?.reservation_hours?.find(
                (d) =>
                  d.day === dayName &&
                  d.enabled
              );

            return (
              <button
                key={formattedDate}
                type="button"
                disabled={!isAvailable}
                onClick={() =>
                  handleEditDateChange(
                    formattedDate
                  )
                }
                className={`rounded-lg border p-2 text-center text-xs transition ${
                  editSelectedDate ===
                  formattedDate
                    ? "bg-red-600 text-white border-red-600"
                    : "bg-white hover:border-gray-400"
                } ${
                  !isAvailable
                    ? "opacity-40 cursor-not-allowed"
                    : ""
                }`}
              >
                <p className="text-xs uppercase">
                  {date.toLocaleDateString(
                    "en-IN",
                    {
                      weekday: "short",
                    }
                  )}
                </p>

                <p className="text-lg font-bold mt-1">
                  {date.getDate()}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* TIME SLOTS */}
      {editAvailableSlots.length > 0 && (
        <div>
          <label className="text-sm font-semibold text-gray-700 mb-3 block">
            Available Time Slots
          </label>

          <div className="grid grid-cols-4 gap-2">
            {editAvailableSlots.map(
              (slot) => {
                const isSelected =
                  editForm.reservation_time ===
                  slot;

                return (
                  <button
                    key={slot}
                    type="button"
                    onClick={() =>
                      setEditForm((prev) => ({
                        ...prev,
                        reservation_time:
                          slot,
                      }))
                    }
                    className={`rounded-lg border py-2 px-2 text-center text-sm font-semibold transition ${
                      isSelected
                        ? "bg-red-600 text-white border-red-600"
                        : "bg-white hover:border-gray-400"
                    }`}
                  >
                    {formatSlotTime(slot)}
                  </button>
                );
              }
            )}
          </div>
        </div>
      )}

      {/* SECTION */}
      {reservationSections.length > 0 && (
        <div>
          <label className="text-sm font-semibold text-gray-700 mb-3 block">
            Preferred Seating Area
          </label>

          <div className="grid grid-cols-3 gap-2">

            <button
              type="button"
              onClick={() =>
                setEditForm({
                  ...editForm,
                  section_id: "",
                })
              }
              className={`border rounded-lg p-3 text-left text-xs transition ${
                !editForm.section_id
                  ? "bg-red-600 text-white border-red-600"
                  : "bg-white hover:border-gray-400"
              }`}
            >
              <p className="font-semibold">
                Any Section
              </p>

              <p className="text-xs opacity-70 mt-1">
                Auto assign
              </p>
            </button>

            {reservationSections.map(
              (section) => (
                <button
                  key={section.id}
                  type="button"
                  onClick={() =>
                    setEditForm({
                      ...editForm,
                      section_id:
                        section.id,
                    })
                  }
                  className={`border rounded-lg p-3 text-left text-xs transition ${
                    editForm.section_id ===
                    section.id
                      ? "bg-red-600 text-white border-red-600"
                      : "bg-white hover:border-gray-400"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{
                        background:
                          section.color ||
                          "gray",
                      }}
                    />

                    <p className="font-semibold">
                      {section.name}
                    </p>
                  </div>

                  <p className="text-xs opacity-70 mt-1">
                    {section.table_count ||
                      0}{" "}
                    tables
                  </p>
                </button>
              )
            )}
          </div>
        </div>
      )}

      {/* CUSTOMER DETAILS */}
      <div className="grid grid-cols-2 gap-4">

        <Input
          placeholder="Full Name *"
          value={editForm.customer_name}
          onChange={(e) =>
            setEditForm({
              ...editForm,
              customer_name:
                e.target.value,
            })
          }
          className="col-span-2"
        />

        <Input
          placeholder="Phone Number *"
          value={editForm.customer_phone}
          onChange={(e) =>
            setEditForm({
              ...editForm,
              customer_phone:
                e.target.value,
            })
          }
        />

        <Input
          placeholder="Email Address"
          type="email"
          value={editForm.customer_email}
          onChange={(e) =>
            setEditForm({
              ...editForm,
              customer_email:
                e.target.value,
            })
          }
        />

        <Input
          placeholder="Number of Guests *"
          type="number"
          min="1"
          value={editForm.party_size}
          onChange={(e) =>
            setEditForm({
              ...editForm,
              party_size:
                parseInt(
                  e.target.value
                ) || 1,
            })
          }
          className="col-span-2"
        />

        <Textarea
          placeholder="Special notes"
          value={editForm.notes}
          onChange={(e) =>
            setEditForm({
              ...editForm,
              notes: e.target.value,
            })
          }
          className="col-span-2"
          rows={3}
        />
      </div>

      {/* ACTIONS */}
      <div className="flex gap-2 pt-4">
        <Button
          variant="outline"
          onClick={() =>
            setEditOpen(false)
          }
          className="flex-1"
        >
          Cancel
        </Button>

        <Button
          onClick={updateReservation}
          className="flex-1 bg-red-600 hover:bg-red-700 text-white"
        >
          Save Changes
        </Button>
      </div>
    </div>
  </DialogContent>
</Dialog>
      </div>
    </AdminLayout>
  );
}